:host([icon-appearance='primary']) {
  a ::slotted([slot='icon']) {
    --wink-icon-color: var(--semantic-color-icon-on-brand);
    --wink-icon-bg-color: var(--semantic-color-background-surface-action-default);
    --wink-icon-md-padding: 0;
  }

  a:hover ::slotted([slot='icon']) {
    --wink-icon-bg-color: var(--semantic-color-brand-default);
  }

  a:visited ::slotted([slot='icon']) {
    --wink-icon-bg-color: var(--semantic-color-background-surface-action-default);
  }

  a:focus ::slotted([slot='icon']) {
    --wink-icon-bg-color: var(--semantic-color-background-surface-action-hover);
  }

  a:active ::slotted([slot='icon']) {
    --wink-icon-bg-color: var(--semantic-color-background-surface-action-active);
  }
}

:host([icon-appearance='secondary']) {
  a ::slotted([slot='icon']) {
    --wink-icon-bg-color: var(--semantic-color-background-surface-bold-default);
    --wink-icon-md-padding: var-rem(--semantic-spacing-none);
    --wink-icon-color: var(--semantic-color-icon-default);
  }

  a:hover ::slotted([slot='icon']) {
    --wink-icon-bg-color: var(--semantic-color-background-surface-bold-hovered);
  }

  a:visited ::slotted([slot='icon']) {
    --wink-icon-bg-color: var(--semantic-color-background-surface-bold-default);
  }

  a:focus ::slotted([slot='icon']) {
    --wink-icon-bg-color: var(--semantic-color-background-surface-bold-hovered);
  }

  a:active ::slotted([slot='icon']) {
    --wink-icon-bg-color: var(--semantic-color-background-surface-bold-pressed);
  }
}

:host {
  .link {
    display: inline-block;
    margin-bottom: var(--wink-link-margin-bottom, var-rem(--semantic-spacing-none));
    padding: var(--wink-link-padding-top, var-rem(--semantic-spacing-none))
      var(--wink-link-padding-right, var-rem(--semantic-spacing-none)) var(--wink-link-padding-bottom, var-rem(--semantic-spacing-none))
      var(--wink-link-padding-left, var-rem(--semantic-spacing-none));
    border-radius: var(--wink-link-border-radius, 0);
    color: inherit;
    text-decoration: none;
    background-color: var(--wink-link-bgcolor--default, var(--semantic-color-background-surface-neutral-default));

    &.font--inherit #link-content {
      font-size: inherit;
      font-weight: inherit;
      line-height: inherit;
      color: inherit;
    }

    &.font--body-regular #link-content {
      @include font--body-regular;
    }

    &.font--body-small #link-content {
      @include font--body-small;
    }

    &.font--caption-bold #link-content {
      @include font--caption-bold;
    }

    &.color--default {
      color: var(--semantic-color-text-default);
    }

    &.color--subtle {
      color: var(--semantic-color-text-subtle);
    }

    &.color--subtlest {
      color: var(--semantic-color-text-subtlest);
    }

    &.color--inverse {
      color: var(--semantic-color-text-inverse);
    }

    &.color--accent {
      color: var(--semantic-color-text-accent-body);
    }

    & #link-content {
      display: block;
      color: var(--wink-link-color--default, var(--semantic-color-text-default));
      text-decoration: underline;
      text-underline-offset: wink-rem(0.3);
    }

    &:hover,
    &:visited:hover {
      cursor: pointer;

      & #link-content {
        color: var(--wink-link-color--hover, var(--semantic-color-text-default));
        text-decoration: none;
      }
    }

    &:active #link-content,
    &:visited:active #link-content {
      color: var(--wink-link-color--active, var(--semantic-color-text-subtlest));
    }

    &:visited #link-content {
      color: var(--wink-link-color--visited, var(--semantic-color-text-default));
    }

    &:hover,
    &:visited:hover,
    &:active,
    &:visited:active,
    &:visited {
      &.font--inherit #link-content {
        color: inherit;
      }
    }

    &:focus {
      outline: none;
    }

    &:focus-visible {
      @include state-focus-outline(var(--wink-link-area--focus--border-radius, wink-rem(0.1)));
    }

    &.disabled,
    &.subtle.disabled {
      @include state-disabled;
    }

    &.subtle {
      & #link-content {
        text-decoration: none;
      }

      &:hover {
        #link-content {
          text-decoration: underline;
        }
      }
    }

    &.link-with-icon {
      display: flex;

      &-left {
        flex-direction: row;

        ::slotted([slot='icon']) {
          align-self: start;
          margin-right: var(--wink-link--with-icon-left-icon--margin-right, var-rem(--semantic-spacing-3xs));
        }

        &.link-with-icon-backdrop {
          ::slotted([slot='icon']) {
            margin-right: var-rem(--semantic-spacing-md);
          }
        }
      }

      &-right {
        flex-direction: row-reverse;
        justify-content: flex-end;

        ::slotted([slot='icon']) {
          align-self: start;
          margin-left: var-rem(--semantic-spacing-3xs);
        }

        &.link-with-icon-backdrop {
          ::slotted([slot='icon']) {
            margin-left: var-rem(--semantic-spacing-md);
          }
        }
      }
    }
  }
}

:host([pointer-event='none']) {
  --wink-link-color--active: #{var(--semantic-color-text-default)};

  &:focus,
  &:focus-within,
  & .link:focus,
  & .link:focus-visible {
    outline-width: 0;
  }

  .link {
    &:hover,
    &:visited:hover {
      cursor: default;
    }

    &:active #link-content,
    &:visited:active #link-content {
      pointer-events: none;
      cursor: default;
    }
  }
}

:host([alignment='left']) * {
  justify-content: flex-start;
  text-align: left;
}

:host([alignment='center']) * {
  justify-content: center;
  text-align: center;
}

:host([alignment='right']) * {
  justify-content: flex-end;
  text-align: right;
}

:host([appearance='area']),
:host([appearance='area']) .link.area,
:host([appearance='area']) #link-content {
  display: var(--wink-link-area--display, block);
  height: var(--wink-link-area__link-content--height, auto);
  text-decoration: none;
  width: var(--wink-link-area--width, auto);

  &:hover {
    text-decoration: none;
  }
}

:host([appearance='dropdown-menu-link']) {
  display: flex;

  .link {
    display: flex;
    flex: 1 0 auto;
    gap: var-rem(--semantic-spacing-sm);
    padding: var-rem(--semantic-spacing-xs) var-rem(--semantic-spacing-sm);
    border-radius: wink-rem(0.4);

    #link-content {
      font-family: var(--semantic-typography-font-family-primary), var(--font-fallbacks);
      font-size: wink-rem(1.6);
      line-height: wink-rem(2.4);
    }

    &:hover {
      background-color: var(--semantic-color-background-surface-neutral-hovered);
    }

    &:active {
      #link-content {
        font-family: var(--semantic-typography-font-family-primary), var(--font-fallbacks);
        font-size: wink-rem(1.6);
        font-weight: 700;
        line-height: wink-rem(2.4);
      }
    }
  }

  .link #link-content {
    text-decoration: none;
  }

  .link.link-with-icon-left.link-with-icon-backdrop ::slotted([slot='icon']) {
    margin-right: var-rem(--semantic-spacing-sm);
  }
}

:host([appearance='dropdown-menu-link'].active) {
  .link {
    background-color: var(--semantic-color-background-surface-neutral-pressed);

    #link-content {
      font-family: var(--semantic-typography-font-family-primary), var(--font-fallbacks);
      font-size: wink-rem(1.6);
      font-weight: var(--semantic-typography-font-weight-medium);
      line-height: wink-rem(2.4);
    }
  }
}

:host([appearance='menu-secondary']),
:host([appearance='menu-single']) {
  @include media-header('from-md') {
    a ::slotted([slot='icon']),
    :active ::slotted([slot='icon']) {
      --wink-icon-color: var(--semantic-color-icon-subtlest);
    }

    a:hover ::slotted([slot='icon']) {
      --wink-icon-color: var(--semantic-color-icon-default);
    }
  }

  & ::slotted([slot='icon']) {
    --wink-link--with-icon-left-icon--margin-right: #{var-rem(--semantic-spacing-xs)};
  }

  a:active ::slotted([slot='icon']) {
    --wink-icon-color: var(--semantic-color-icon-subtle);
  }

  .link {
    --wink-link-padding-top: #{var-rem(--semantic-spacing-xs)};
    --wink-link-padding-bottom: #{var-rem(--semantic-spacing-xs)};

    text-decoration: none;

    & #link-content {
      --wink-link-color--active: var(--semantic-color-text-subtlest);

      height: wink-rem(2);
      text-decoration: none;

      @include media-header('from-md') {
        --wink-link-color--default: var(--semantic-color-text-subtlest);
        --wink-link-color--hover: var(--semantic-color-text-default);
        --wink-link-color--visited: var(--semantic-color-text-subtlest);
      }
    }

    @include media-header('from-md') {
      --wink-link-padding-left: #{var-rem(--semantic-spacing-xs)};
      --wink-link-padding-right: #{var-rem(--semantic-spacing-xs)};
      --wink-link-bgcolor--default: unset;
      --wink-link-border-radius: #{wink-rem(0.4)};

      &.font--body-regular #link-content {
        font-size: wink-rem(1.4);
        line-height: normal;
      }
    }
  }
}

:host([appearance='menu-single']) {
  .link {
    display: flex;
    align-items: center;
    height: wink-rem(1.6);
    margin-top: var-rem(--semantic-border-md);

    &:active #link-content,
    &:visited:active #link-content {
      --wink-link-color--active: var(--semantic-components-global-header-color-text-link);

      font-weight: var(--semantic-components-global-header-typography-font-weight-link);
    }
  }
}

:host([appearance='menu-secondary']) {
  .link {
    margin-top: var-rem(--semantic-spacing-3xs);
  }
}

:host([appearance='menu-back']) {
  a ::slotted([slot='icon']),
  a:hover ::slotted([slot='icon']),
  a:visited ::slotted([slot='icon']) {
    --wink-icon-color: var(--semantic-color-icon-subtle);
  }

  a:active ::slotted([slot='icon']) {
    --wink-icon-color: var(--semantic-color-icon-subtlest);
  }

  .link {
    align-items: center;
    text-decoration: none;

    & #link-content {
      --wink-link-color--default: var(--semantic-color-text-subtle);
      --wink-link-color--hover: var(--semantic-color-text-subtle);
      --wink-link-color--visited: var(--semantic-color-text-subtle);
      --wink-link-color--active: var(--semantic-color-text-subtlest);

      text-decoration: none;
    }
  }
}

:host([appearance='in-page-navigation']) {
  position: relative;
  padding: var-rem(--semantic-spacing-xs) 0 var-rem(--semantic-spacing-xs) var-rem(--semantic-spacing-md);
  border-left: var-rem(--semantic-border-md) solid var(--wink-in-page-border, var(--semantic-color-border-default));

  &:has(.link.active) {
    border-left-color: var(--wink-in-page-border-highlight, var(--semantic-color-border-highlight));
  }

  .link {
    #link-content {
      @include font--body-small;

      text-decoration: none;

      &:hover {
        text-decoration: underline;
      }
    }

    &.active {
      font-weight: var(--semantic-typography-font-weight-medium);
    }
  }
}
